import { sanitizeEmptyStrings } from "@clintonelec/typescriptutils";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createAppSlice } from "Data/Redux/Helpers";
import type { ApiHostname, HostnameBase, HostnameState, DataPage, WithApiData } from "Interfaces";
import * as HostnamesApi from "Data/API/Hostnames";
import { deserializeJsonApiData } from "Data/Utils/JsonApi";
import {
	createOrUpdatePaginatedItem, createPaginatedItem, deletePaginatedItem, filterPaginatedDataByUuid,
	handleClearCache, handleDataPage, updatePaginatedItem
} from "Data/Utils/Redux";
import { clearCacheAction } from "Data/Actions/System";
import { selectToken } from "Data/Redux/Slices/User";
import type { AppState } from "Data/Redux/Reducers";

const initialState: HostnameState = {
	data: [],
	lastPage: -1,
	options: {
		"page[number]": 1
	}
};

// #region Reducers

const setHostnameOptionsReducer = (
	state: HostnameState, action: PayloadAction<Record<string, string | number>>
) => {
	state.options = action.payload;
	state.data = [];
	state.lastPage = -1;
};

const createHostnamePusher = (payload: WithApiData<ApiHostname>) => {
	return deserializeJsonApiData<ApiHostname>(payload);
};

const updateHostnamePusher = (payload: WithApiData<ApiHostname>) => {
	return deserializeJsonApiData<ApiHostname>(payload);
};

const deleteHostnamePusher = (payload: string) => payload;

// #endregion

// #region Getters

export const getHostnamesOptions = (state: HostnameState) => {
	return state.options;
};

export const getHostnames = (state: HostnameState) => {
	return state.data;
};

export const getHostnameById = (state: HostnameState, id: string) => {
	return filterPaginatedDataByUuid(state, id);
};

export const getHostnameCount = (state: HostnameState) => {
	return state.data.length;
};

// #endregion

const hostnamesSlice = createAppSlice({
	name: "hostnames",
	initialState,
	reducers: (builder) => ({
		setHostnameOptionsAction: builder.reducer(setHostnameOptionsReducer),
		createHostnamePusherAction: builder.asyncThunk(createHostnamePusher, {
			fulfilled: createPaginatedItem
		}),
		updateHostnamePusherAction: builder.asyncThunk(updateHostnamePusher, {
			fulfilled: updatePaginatedItem
		}),
		deleteHostnamePusherAction: builder.asyncThunk(deleteHostnamePusher, {
			fulfilled: deletePaginatedItem
		}),
		createHostnameAction: builder.asyncThunk((payload: HostnameBase, thunkApi): Promise<ApiHostname> => {
			const sanitizedPayload = sanitizeEmptyStrings(payload) as ApiHostname;
			// https://redux-toolkit.js.org/usage/usage-with-typescript#typing-async-thunks-inside-createslice
			const state = thunkApi.getState() as AppState;

			return HostnamesApi.createHostname(sanitizedPayload, selectToken(state) ?? "");
		}, {
			fulfilled: createPaginatedItem
		}),
		updateHostnameAction: builder.asyncThunk((payload: ApiHostname, thunkApi): Promise<ApiHostname> => {
			const sanitizedPayload = sanitizeEmptyStrings(payload) as ApiHostname;
			const state = thunkApi.getState() as AppState;

			return HostnamesApi.updateHostname(sanitizedPayload, selectToken(state) ?? "");
		}, {
			fulfilled: updatePaginatedItem
		}),
		deleteHostnameAction: builder.asyncThunk((payload: string, thunkApi): Promise<string> => {
			return HostnamesApi.deleteHostname(payload, selectToken(thunkApi.getState() as AppState) ?? "")
				.then(() => payload);
		}, {
			fulfilled: deletePaginatedItem
		}),
		fetchHostnamesPageAction: builder.asyncThunk((payload: void, thunkApi): Promise<DataPage<ApiHostname>> => {
			const state = thunkApi.getState() as AppState;
			const { hostnames: hostnamesState } = state;

			return HostnamesApi.fetchHostnames(getHostnamesOptions(hostnamesState), selectToken(state) ?? "");
		}, {
			fulfilled: handleDataPage
		}),
		fetchHostnameAction: builder.asyncThunk((payload: string, thunkApi): Promise<ApiHostname> => {
			return HostnamesApi.fetchHostname(payload, selectToken(thunkApi.getState() as AppState) ?? "");
		}, {
			fulfilled: createOrUpdatePaginatedItem
		})
	}),
	selectors: {
		selectHostnames: getHostnames,
		selectHostnameById: getHostnameById,
		selectHostnamesOptions: getHostnamesOptions,
		selectHostnameCount: getHostnameCount
	},
	extraReducers: (builder) => {
		builder.addCase(clearCacheAction, handleClearCache(initialState));
	}
});

export default hostnamesSlice;

export const {
	createHostnameAction, createHostnamePusherAction, deleteHostnameAction, deleteHostnamePusherAction,
	setHostnameOptionsAction, updateHostnameAction, updateHostnamePusherAction, fetchHostnameAction,
	fetchHostnamesPageAction
} = hostnamesSlice.actions;

export const {
	selectHostnameById, selectHostnameCount, selectHostnames, selectHostnamesOptions
} = hostnamesSlice.selectors;
